<template>
  <v-card>
    <v-card-title class="justify-space-between pa-1">
      <v-container fluid>
        <v-row>
          <v-col
            cols="12"
            lg="3"
            md="3"
            sm="12"
          >
            <v-select
              v-model="validate"
              class="py-0"
              :items="validateOptions"
              no-data-text="無搜尋結果"
              outlined
              dense
              hide-details
            />
          </v-col>
          <v-col cols="1">
            <task-create-dialog
              v-if="canCreate"
              :specs="specs"
              :categories="categories"
              @update="emit('update')"
            >
              <template #icon>
                fas fa-plus
              </template>
            </task-create-dialog>
          </v-col>
          <v-spacer />
          <v-col
            cols="10"
            lg="4"
            md="4"
            sm="12"
          >
            <v-text-field
              v-model="search"
              class="py-0"
              label="Search"
              single-line
              hide-details
            >
              <v-icon
                slot="append"
                small
              >
                fas fa-search
              </v-icon>
            </v-text-field>
          </v-col>
        </v-row>
      </v-container>
    </v-card-title>
    <v-divider />
    <v-card-text class="pa-0">
      <v-data-table
        :loading="loading"
        :headers="headers"
        :items="items"
        :search="search"
        :items-per-page="20"
        class="elevation-1"
        :custom-filter="filter"
        show-expand
      >
        <template #item="{item, isExpanded, expand}">
          <tr>
            <td class="px-2">
              <v-btn
                v-if="!isExpanded"
                icon
                @click="expand(!isExpanded)"
              >
                <v-icon>fas fa-chevron-down</v-icon>
              </v-btn>
              <v-btn
                v-else
                icon
                @click="expand(!isExpanded)"
              >
                <v-icon>fas fa-chevron-up</v-icon>
              </v-btn>
            </td>
            <td>
              <span>{{ item.id }}</span>
            </td>
            <td>
              <span>{{ item.spec.category.name }}</span>
            </td>
            <td>
              <span>{{ item.owner.name }}</span>
            </td>
            <td>
              <span>{{ item.name }}</span>
            </td>
            <td>
              <span>{{ item.cron }}</span>
            </td>
            <td>
              <task-active-dialog
                :task="item"
                @update="update"
              />
            </td>
            <td>
              <span>{{ item.updatedAt }}</span>
            </td>
            <td>
              <span>
                <task-edit-dialog
                  v-if="canEdit"
                  :task="item"
                  :specs="specs"
                  :categories="categories"
                  @update="update"
                />
                <task-exec-dialog
                  v-if="canExec"
                  :task="item"
                />
                <task-create-dialog
                  v-if="canCreate"
                  :task="item"
                  :specs="specs"
                  :categories="categories"
                >
                  <template #icon>fas fa-copy</template>
                </task-create-dialog>
                <task-delete-dialog
                  v-if="canDelete"
                  :task="item"
                  @update="update"
                />
              </span>
            </td>
          </tr>
        </template>
        <template #expanded-item="{ item, headers: expandHeaders }">
          <v-expand-x-transition>
            <td :colspan="expandHeaders.length">
              <v-container>
                <v-row>
                  <v-col cols="2">
                    <v-text-field
                      :disabled="true"
                      :hide-details="true"
                      :outlined="true"
                      :filled="true"
                      :dense="true"
                      label="SpecName"
                      :value="item.spec.name"
                    />
                  </v-col>
                  <v-col cols="2">
                    <v-text-field
                      :disabled="true"
                      :hide-details="true"
                      :outlined="true"
                      :filled="true"
                      :dense="true"
                      label="Method"
                      :value="item.spec.method"
                    />
                  </v-col>
                  <v-col cols="5">
                    <v-text-field
                      :disabled="true"
                      :hide-details="true"
                      :outlined="true"
                      :filled="true"
                      :dense="true"
                      label="Endpoint"
                      :value="item.spec.endpoint"
                    />
                  </v-col>
                  <v-col cols="3">
                    <v-text-field
                      :disabled="true"
                      :hide-details="true"
                      :outlined="true"
                      :filled="true"
                      :dense="true"
                      label="Timezone"
                      :value="item.timezone"
                    />
                  </v-col>
                </v-row>
                <v-row>
                  <v-col>
                    <v-textarea
                      :disabled="true"
                      :hide-details="true"
                      :outlined="true"
                      :filled="true"
                      :dense="false"
                      auto-grow
                      name="headers"
                      label="Headers"
                      :value="JSON.stringify(item.headers, null, 4)"
                    />
                  </v-col>
                </v-row>
                <v-row>
                  <v-col>
                    <v-textarea
                      :disabled="true"
                      :hide-details="true"
                      :outlined="true"
                      :filled="true"
                      :dense="false"
                      auto-grow
                      name="payload"
                      label="Payload"
                      :value="JSON.stringify(item.payload, null, 4)"
                    />
                  </v-col>
                </v-row>
              </v-container>
            </td>
          </v-expand-x-transition>
        </template>
      </v-data-table>
    </v-card-text>
  </v-card>
</template>

<script>
import { Scheduler } from '@rhitics/core'
import TaskCreateDialog from '@rhitics/scheduler/components/Task/TaskCreateDialog.vue'
import TaskEditDialog from '@rhitics/scheduler/components/Task/TaskEditDialog.vue'
import TaskDeleteDialog from '@rhitics/scheduler/components/Task/TaskDeleteDialog.vue'
import TaskExecDialog from '@rhitics/scheduler/components/Task/TaskExecDialog.vue'
import TaskActiveDialog from '@rhitics/scheduler/components/Task/TaskActiveDialog.vue'
import { cloneDeep } from 'lodash'
import { EDIT_CRAWLER_TASK, REMOVE_CRAWLER_TASK, CREATE_CRAWLER_TASK, EXEC_CRAWLER_TASK } from '../constants/policies'

export default {
  name: 'CrawlerTaskTable',

  components: {
    TaskActiveDialog,
    TaskExecDialog,
    TaskCreateDialog,
    TaskEditDialog,
    TaskDeleteDialog,
  },
  props: {
    loading: {
      type: Boolean,
      default: false
    },
    tasks: {
      type: Array
    },
    specs: {
      type: Array
    },
    categories: {
      type: Array
    }
  },
  data() {
    return {
      dialog: false,
      items: [],
      selected: {},
      search: '',
      validateOptions: [
        { text: 'All', value: 'all' },
        { text: 'Active', value: 1 },
        { text: 'Not active', value: 0 }
      ],
      validate: 'all'
    }
  },

  computed: {
    headers() {
      return [
        { text: 'ID', value: 'id' },
        { text: 'Category', value: 'categoryName' },
        { text: 'Owner', value: 'owner' },
        { text: 'Name', value: 'name' },
        { text: 'Cron', value: 'cron' },
        { text: 'Status', value: 'active', align: 'center' },
        { text: 'UpdatedAt', value: 'updatedAt' },
        { text: 'Action', value: 'action', sortable: false }
      ]
    },
    canEdit() {
      return this.$auth.can(EDIT_CRAWLER_TASK)
    },
    canDelete() {
      return this.$auth.can(REMOVE_CRAWLER_TASK)
    },
    canCreate() {
      return this.$auth.can(CREATE_CRAWLER_TASK)
    },
    canExec() {
      return this.$auth.can(EXEC_CRAWLER_TASK)
    }
  },

  watch: {
    validate() {
      this.items = cloneDeep(this.tasks)
      this.items = this.items.filter(v => this.validate === 'all' || v.active === this.validate)
    },
    tasks() {
      this.items = cloneDeep(this.tasks)
    }
  },

  methods: {
    update() {
      this.$emit('update')
    },
    filter(value, search, item) {
      return (
        item.spec.category.name.includes(search) ||
        item.name.includes(search) ||
        item.cron.includes(search) ||
        item.owner.name.includes(search) ||
        item.updatedAt.includes(search)
      )
    }
  }
}
</script>
